(() => {
  const STORAGE_KEY = 'mcor_fit_guide_vehicle_v1';
  const PARAMS = {
    year: 'fg_year',
    make: 'fg_make',
    model: 'fg_model',
    submodel: 'fg_submodel',
    body_style: 'fg_body',
    screen: 'fg_screen',
    amplifier: 'fg_amp',
    hvac: 'fg_hvac',
    key_type: 'fg_key',
    transmission: 'fg_trans',
    powertrain: 'fg_powertrain',
  };

  const readVehicle = () => {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const hasVehicle = (vehicle) => Boolean(vehicle?.year && vehicle?.make && vehicle?.model);

  const fitGuideUrl = (root, vehicle) => {
    const url = new URL(root.dataset.fitGuideUrl || '/pages/speaker-fit-guide', window.location.origin);
    Object.entries(PARAMS).forEach(([field, parameter]) => {
      if (vehicle[field]) url.searchParams.set(parameter, vehicle[field]);
    });
    return `${url.pathname}${url.search}`;
  };

  const render = () => {
    const root = document.getElementById('McorVehicleContext');
    if (!root) return;

    const vehicle = readVehicle();
    if (!hasVehicle(vehicle)) {
      root.hidden = true;
      return;
    }

    root.querySelector('[data-mcor-vehicle-label]').textContent = [vehicle.year, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(' ');

    const details = [vehicle.submodel, vehicle.body_style]
      .filter((value, index, values) => value && values.indexOf(value) === index)
      .join(' • ');
    const detailsElement = root.querySelector('[data-mcor-vehicle-details]');
    detailsElement.textContent = details;
    detailsElement.hidden = !details;

    root.querySelector('[data-mcor-vehicle-change]').href = fitGuideUrl(root, vehicle);
    root.hidden = false;
  };

  document.addEventListener('DOMContentLoaded', render, { once: true });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) render();
  });
  document.addEventListener('mcor:vehicle-changed', render);
})();

(() => {
  const SYSTEM_ID = '_mcor_system_id';
  const SYSTEM_MANIFEST = '_mcor_system_manifest';
  const CHECKOUT_SELECTOR = '[name="checkout"], #checkout, #CartDrawer-Checkout, a[href^="/checkout"]';
  let lastValidation = { valid: true, incompleteSystems: [] };
  let refreshTimer = null;

  const property = (item, name) => String(item?.properties?.[name] || '').trim();
  const parseManifest = (value) => [...new Set(String(value || '').split('|').map(valuePart => valuePart.trim()).filter(Boolean))].sort();

  const validateCart = (cart) => {
    const systems = new Map();
    for (const item of cart?.items || []) {
      const systemId = property(item, SYSTEM_ID);
      if (!systemId) continue;
      if (!systems.has(systemId)) systems.set(systemId, []);
      systems.get(systemId).push(item);
    }

    const incompleteSystems = [];
    for (const [systemId, items] of systems) {
      const manifests = items.map(item => parseManifest(property(item, SYSTEM_MANIFEST)));
      const expected = manifests[0] || [];
      const manifestKey = expected.join('|');
      const consistentManifest = expected.length > 1 && manifests.every(manifest => manifest.join('|') === manifestKey);
      const quantities = new Map(expected.map(id => [id, 0]));
      let containsUnexpectedItem = false;

      for (const item of items) {
        const variantId = String(item.variant_id || item.id || '');
        if (!quantities.has(variantId)) containsUnexpectedItem = true;
        else quantities.set(variantId, quantities.get(variantId) + Number(item.quantity || 0));
      }

      const requiredQuantities = [...quantities.values()];
      const completeQuantities = requiredQuantities.length === expected.length
        && requiredQuantities.every(quantity => quantity > 0)
        && new Set(requiredQuantities).size === 1;

      if (!consistentManifest || containsUnexpectedItem || !completeQuantities) {
        incompleteSystems.push({ systemId, expected, present: items.map(item => String(item.variant_id || item.id || '')) });
      }
    }

    return { valid: incompleteSystems.length === 0, incompleteSystems };
  };

  const warningFor = (button) => {
    const container = button.closest('.cart__ctas, .cart__footer, .drawer__footer, form') || button.parentElement;
    if (!container) return null;
    let warning = container.querySelector('[data-mcor-system-warning]');
    if (!warning) {
      warning = document.createElement('div');
      warning.className = 'mcor-system-warning';
      warning.dataset.mcorSystemWarning = '';
      warning.setAttribute('role', 'alert');
      warning.innerHTML = '<strong>Complete system required</strong><span>This Kenwood Direct-Fit system will not operate with a required component missing. Restore the missing item or remove the remaining marked system components before checkout.</span>';
      container.insertBefore(warning, button);
    }
    return warning;
  };

  const applyCheckoutState = (validation) => {
    lastValidation = validation;
    const blocked = !validation.valid;
    document.documentElement.classList.toggle('mcor-system-cart-incomplete', blocked);

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(button => {
      if ('disabled' in button) button.disabled = blocked;
      button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      button.classList.toggle('mcor-system-checkout-blocked', blocked);
      const warning = warningFor(button);
      if (warning) warning.hidden = !blocked;
    });

    document.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons, shopify-accelerated-checkout').forEach(element => {
      element.hidden = blocked;
      element.setAttribute('aria-hidden', blocked ? 'true' : 'false');
    });
  };

  const refresh = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart could not be verified.');
      applyCheckoutState(validateCart(await response.json()));
    } catch (error) {
      console.warn('[MCOR] Complete-system cart verification failed.', error);
    }
  };

  const scheduleRefresh = (delay = 120) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  };

  const isCheckoutTarget = (target) => target instanceof Element && Boolean(target.closest(CHECKOUT_SELECTOR));
  document.addEventListener('click', event => {
    if (!lastValidation.valid && isCheckoutTarget(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('[data-mcor-system-warning]:not([hidden])')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (event.target instanceof Element && event.target.closest('cart-remove-button, quantity-input, [name="updates[]"], [data-cart-remove]')) scheduleRefresh(450);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    const checkoutSubmission = isCheckoutTarget(submitter) || /\/checkout(?:$|[?#])/i.test(form?.action || '');
    if (!lastValidation.valid && checkoutSubmission) {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('[data-mcor-system-warning]:not([hidden])')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, true);

  const observer = new MutationObserver(() => {
    if (!lastValidation.valid) applyCheckoutState(lastValidation);
  });

  window.MCORSystemCartGuard = { validateCart, refresh };
  window.addEventListener('mcor:cart-updated', () => scheduleRefresh(0));
  window.addEventListener('pageshow', () => scheduleRefresh(0));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(0); });
  const start = () => {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    refresh();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
